import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCreditCard, FiLoader, FiCheck, FiAlertCircle, FiCopy, FiCheckCircle } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { cartaoPublicoAPI } from '../services/api';
import Footer from '../components/Footer';
import theme from '../styles/theme';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[6]};
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  max-width: 700px;
  width: 100%;
  padding: ${theme.spacing[8]};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[500]}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[4]};
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.primary[500]};
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin-bottom: ${theme.spacing[2]};
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
  margin-left: ${theme.spacing[1]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: ${props => props.variant === 'secondary' ? 'transparent' : theme.colors.primary[500]};
  border: 1px solid ${props => props.variant === 'secondary' ? theme.colors.border.primary : theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${props => props.variant === 'secondary' ? theme.colors.text.primary : theme.colors.white};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'secondary' ? theme.colors.background.primary : theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.error[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const SuccessMessage = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.success[500]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border.primary};
  border-top: 3px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ClienteInfo = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[6]};
  text-align: center;
`;

const CadastrarCartaoPublico = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [formData, setFormData] = useState({
    holder_name: '',
    number: '', // Número completo do cartão
    ccv: '', // Código de segurança
    expiry_month: '',
    expiry_year: '',
    holder_cpf_cnpj: '',
    holder_email: '',
    holder_mobile_phone: '',
    holder_postal_code: '',
    holder_address: '',
    holder_address_number: '',
    holder_address_complement: ''
  });

  useEffect(() => {
    const validarToken = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Token recebido na URL:', token);
        console.log('Tamanho do token:', token?.length);
        const response = await cartaoPublicoAPI.validarToken(token);
        
        if (response.data.success) {
          setClienteInfo(response.data.data);
        } else {
          setError(response.data.message || 'Token inválido');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Erro ao validar token. Verifique se o link está correto.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validarToken();
    } else {
      setError('Token não fornecido');
      setLoading(false);
    }
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const aplicarMascaraCPF = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // CPF: 11 dígitos (000.000.000-00)
    if (apenasNumeros.length <= 11) {
      return apenasNumeros
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    
    // CNPJ: 14 dígitos (00.000.000/0000-00)
    if (apenasNumeros.length <= 14) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
    
    // Limitar a 14 dígitos
    return apenasNumeros.slice(0, 14)
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const aplicarMascaraTelefone = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Telefone fixo: 10 dígitos (00) 0000-0000
    if (apenasNumeros.length <= 10) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    
    // Celular: 11 dígitos (00) 00000-0000
    if (apenasNumeros.length <= 11) {
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    // Limitar a 11 dígitos
    return apenasNumeros.slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  const aplicarMascaraCEP = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // CEP: 8 dígitos (00000-000)
    if (apenasNumeros.length <= 8) {
      return apenasNumeros.replace(/(\d{5})(\d)/, '$1-$2');
    }
    
    // Limitar a 8 dígitos
    return apenasNumeros.slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
  };

  const aplicarMascaraNumeroCartao = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Limitar a 19 dígitos (máximo para cartões)
    const numerosLimitados = apenasNumeros.slice(0, 19);
    
    // Formatar com espaços a cada 4 dígitos
    return numerosLimitados.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatarNomeTitular = (valor) => {
    // Remove números e caracteres especiais, mantém apenas letras, espaços e acentos
    return valor.replace(/[0-9]/g, '').replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.holder_name || !formData.number || !formData.ccv || !formData.expiry_month || 
        !formData.expiry_year || !formData.holder_cpf_cnpj) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar nome do titular: deve ter pelo menos nome e sobrenome
    const nomeLimpo = formData.holder_name.trim();
    const partesNome = nomeLimpo.split(/\s+/).filter(parte => parte.length > 0);
    if (partesNome.length < 2) {
      setError('O nome do titular deve conter pelo menos nome e sobrenome');
      return;
    }

    const cardNumberClean = formData.number.replace(/\D/g, '');
    if (cardNumberClean.length < 13 || cardNumberClean.length > 19) {
      setError('Número do cartão inválido');
      return;
    }

    const ccvClean = formData.ccv.replace(/\D/g, '');
    if (ccvClean.length < 3 || ccvClean.length > 4) {
      setError('CCV inválido (deve ter 3 ou 4 dígitos)');
      return;
    }

    if (!formData.expiry_month) {
      setError('Selecione o mês de validade');
      return;
    }

    if (!formData.expiry_year) {
      setError('Selecione o ano de validade');
      return;
    }

    // Validar email
    if (!formData.holder_email || !formData.holder_email.trim()) {
      setError('Email do titular é obrigatório');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.holder_email.trim())) {
      setError('Email inválido');
      return;
    }

    // Validar telefone celular
    if (!formData.holder_mobile_phone || !formData.holder_mobile_phone.trim()) {
      setError('Telefone celular é obrigatório');
      return;
    }

    const telefoneClean = formData.holder_mobile_phone.replace(/\D/g, '');
    if (telefoneClean.length !== 11) {
      setError('Telefone celular inválido (deve ter 11 dígitos)');
      return;
    }

    // Validar CEP
    if (!formData.holder_postal_code || !formData.holder_postal_code.trim()) {
      setError('CEP é obrigatório');
      return;
    }

    const cepClean = formData.holder_postal_code.replace(/\D/g, '');
    if (cepClean.length !== 8) {
      setError('CEP inválido (deve ter 8 dígitos)');
      return;
    }

    // Validar endereço
    if (!formData.holder_address || !formData.holder_address.trim()) {
      setError('Endereço é obrigatório');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await cartaoPublicoAPI.salvarCartao(token, formData);
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          // Redirecionar após 3 segundos
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Erro ao salvar cartão');
      }
    } catch (err) {
      // Extrair mensagem de erro da resposta da API
      let errorMessage = 'Erro ao salvar cartão';
      
      if (err.response?.data) {
        // Se tiver mensagem direta
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        // Se tiver array de erros
        else if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors
            .map(e => e.description || e.message || 'Erro desconhecido')
            .join('; ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <LoadingContainer>
              <LoadingSpinner />
              <Subtitle>Validando link...</Subtitle>
            </LoadingContainer>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  if (error && !clienteInfo) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <Header>
              <Icon>
                <FiAlertCircle />
              </Icon>
              <Title>Erro</Title>
              <ErrorMessage>
                <FiAlertCircle />
                {error}
              </ErrorMessage>
            </Header>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  if (success) {
    return (
      <Container>
        <ContentWrapper>
          <Card>
            <Header>
              <Icon style={{ background: `${theme.colors.success[500]}20`, color: theme.colors.success[500] }}>
                <FiCheckCircle />
              </Icon>
              <Title>Cartão Cadastrado com Sucesso!</Title>
              <Subtitle>Você será redirecionado em instantes...</Subtitle>
            </Header>
          </Card>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <Card>
          <Header>
            <Icon>
              <FiCreditCard />
            </Icon>
            <Title>Cadastro de Cartão de Crédito</Title>
            {clienteInfo && (
              <ClienteInfo>
                <Subtitle>Cliente: <strong>{clienteInfo.cliente_nome}</strong></Subtitle>
                {clienteInfo.valor_mensal && (
                  <Subtitle style={{ marginTop: theme.spacing[2], fontSize: theme.fontSizes.lg, fontWeight: 600 }}>
                    Valor a ser cobrado: <strong style={{ color: theme.colors.primary[500] }}>
                      R$ {clienteInfo.valor_mensal.toFixed(2).replace('.', ',')}
                    </strong>
                  </Subtitle>
                )}
              </ClienteInfo>
            )}
          </Header>

          {error && (
            <ErrorMessage>
              <FiAlertCircle />
              {error}
            </ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <Grid>
              <FormGroup>
                <Label>
                  Nome completo como está no cartão
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_name}
                  onChange={(e) => handleChange('holder_name', formatarNomeTitular(e.target.value))}
                  disabled={saving}
                  placeholder="Nome completo"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Número do Cartão
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.number}
                  onChange={(e) => handleChange('number', aplicarMascaraNumeroCartao(e.target.value))}
                  disabled={saving}
                  placeholder="1234 5678 9012 3456"
                  maxLength={23} // 16 dígitos + 3 espaços (ou 19 dígitos + 4 espaços)
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Código de Segurança (CCV)
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.ccv}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    handleChange('ccv', value);
                  }}
                  disabled={saving}
                  placeholder="123"
                  maxLength={4}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Mês de Validade
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.expiry_month}
                  onChange={(e) => handleChange('expiry_month', e.target.value)}
                  disabled={saving}
                  required
                >
                  <option value="">Selecione o mês</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month.toString().padStart(2, '0')}>
                      {month.toString().padStart(2, '0')}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  Ano de Validade
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.expiry_year}
                  onChange={(e) => handleChange('expiry_year', e.target.value)}
                  disabled={saving}
                  required
                >
                  <option value="">Selecione o ano</option>
                  {Array.from({ length: 16 }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return (
                      <option key={year} value={year.toString()}>
                        {year}
                      </option>
                    );
                  })}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  CPF/CNPJ do Titular
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_cpf_cnpj}
                  onChange={(e) => handleChange('holder_cpf_cnpj', aplicarMascaraCPF(e.target.value))}
                  disabled={saving}
                  placeholder="000.000.000-00"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Email do Titular
                  <Required>*</Required>
                </Label>
                <Input
                  type="email"
                  value={formData.holder_email}
                  onChange={(e) => handleChange('holder_email', e.target.value)}
                  disabled={saving}
                  placeholder="email@exemplo.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Telefone Celular
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_mobile_phone}
                  onChange={(e) => handleChange('holder_mobile_phone', aplicarMascaraTelefone(e.target.value))}
                  disabled={saving}
                  placeholder="(00) 00000-0000"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  CEP
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_postal_code}
                  onChange={(e) => handleChange('holder_postal_code', aplicarMascaraCEP(e.target.value))}
                  disabled={saving}
                  placeholder="00000-000"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Endereço
                  <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  value={formData.holder_address}
                  onChange={(e) => handleChange('holder_address', e.target.value)}
                  disabled={saving}
                  placeholder="Rua, Avenida, etc."
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Número
                </Label>
                <Input
                  type="text"
                  value={formData.holder_address_number}
                  onChange={(e) => handleChange('holder_address_number', e.target.value)}
                  disabled={saving}
                  placeholder="123"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Complemento
                </Label>
                <Input
                  type="text"
                  value={formData.holder_address_complement}
                  onChange={(e) => handleChange('holder_address_complement', e.target.value)}
                  disabled={saving}
                  placeholder="Apto, Sala, etc."
                />
              </FormGroup>
            </Grid>

            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <FiLoader style={{ animation: 'spin 1s linear infinite' }} />
                  Salvando...
                </>
              ) : (
                <>
                  <FiCreditCard />
                  Salvar Cartão
                </>
              )}
            </Button>
          </Form>
        </Card>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default CadastrarCartaoPublico;

